/**
 * RecordTypePickerModal — wspólne okno wyboru typu rekordu kartoteki (dokumentów, zadań) przed
 * formularzem nowego rekordu oraz przy akcji „Zmień typ".
 *
 * Okno jest CZYSTYM widokiem: dostaje gotową listę typów (id, nazwa, kod, firma i jej nazwa,
 * opcjonalnie opis i znaczek pochodzenia) oraz napisy, a samo robi filtr firmy, kolumnowe
 * wyszukiwanie po kodzie i nazwie, wybór wiersza, opcjonalne ulubione (gwiazdka + przełącznik
 * „tylko ulubione / pełna lista") i przycisk potwierdzenia. Skąd lista się bierze (typy dokumentów
 * z zawężeniem z menu, słownik typów zadań) i które napisy pokazać — rozstrzygają cienkie nakładki:
 * `DocumentTypePickerModal`, `TaskTypePickerModal`.
 *
 * Wiersz = typ + firma, do której należy: ten sam typ globalny dokumentu bywa zmaterializowany w
 * kilku firmach pod jednym id, więc klucz wiersza i test-id są złożone (`<id>:<companyId>`),
 * a `onSelect` oddaje parę (id typu, id firmy wiersza).
 *
 * Ulubione są opcjonalne: bez `favorites` okno nie ma kolumny z gwiazdką ani przełącznika, a lista
 * jest zawsze pełna.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Star } from 'lucide-react';

import { Button } from '@/components/Button';
import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';

/** Typ do wyboru — pozycja listy okna. */
export interface RecordTypeOption {
  id: string;
  name: string;
  code: string;
  companyId: string;
  /** Nazwa firmy typu (kolumna „Firma" i filtr firmy). */
  companyName: string;
  description?: string | null;
  /** Znaczek pochodzenia przy kodzie (np. „Systemowy" / „Własny"); brak = bez znaczka. */
  origin?: { label: string; isGlobal: boolean };
}

/** Ulubione typy użytkownika (przypięcia) — opcjonalna funkcja okna. */
export interface RecordTypeFavorites {
  pinnedIds: ReadonlySet<string>;
  isLoading: boolean;
  pin: (typeId: string) => void;
  unpin: (typeId: string) => void;
}

/** Napisy okna — już przetłumaczone przez nakładkę. */
export interface RecordTypePickerLabels {
  title: string;
  confirm: string;
  /** Podpowiedź nad listą, widoczna gdy filtr firmy jest pusty („wszystkie firmy"). */
  allCompaniesHint: string;
  /** Komunikat, gdy lista typów jest pusta (przed filtrami tekstowymi). */
  empty: string;
  colCode: string;
  colName: string;
  colCompany: string;
  colDescription: string;
  filterCode: string;
  filterName: string;
  filterCompany: string;
  /** Napisy ulubionych — wymagane tylko z `favorites`. */
  favorites?: {
    toggleShowPinned: string;
    toggleShowFull: string;
    pinTooltip: string;
    unpinTooltip: string;
  };
}

export interface RecordTypePickerModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (typeId: string, companyId: string) => void;
  /** Przedrostek test-id i nazwy grupy radio (np. `document-type-picker`). */
  testIdPrefix: string;
  labels: RecordTypePickerLabels;
  types: ReadonlyArray<RecordTypeOption>;
  isLoading: boolean;
  isError: boolean;
  /** Firmy użytkownika — do ustawienia domyślnego filtra firmy po nazwie. */
  companies: ReadonlyArray<{ id: string; name: string }>;
  /** Firma, której nazwa trafia do pola filtra przy otwarciu; `null` = brak zawężenia. */
  defaultFilterCompanyId?: string | null;
  /** Typ wstępnie zaznaczany przy otwarciu (np. obecny typ dokumentu). */
  initialTypeId?: string | null;
  /** Gdy podana, wstępne zaznaczenie wymaga typu tej firmy. */
  initialCompanyId?: string | null;
  favorites?: RecordTypeFavorites;
}

/** Wybrany wiersz — typ + firma, do której należy. */
interface SelectedType {
  id: string;
  companyId: string;
}

const EMPTY_PINNED: ReadonlySet<string> = new Set<string>();

const rowKey = (type: RecordTypeOption): string => `${type.id}:${type.companyId}`;

export function RecordTypePickerModal({
  open,
  onClose,
  onSelect,
  testIdPrefix,
  labels,
  types,
  isLoading,
  isError,
  companies,
  defaultFilterCompanyId = null,
  initialTypeId = null,
  initialCompanyId = null,
  favorites,
}: RecordTypePickerModalProps) {
  const { t } = useTranslation();

  const pinnedIds = favorites?.pinnedIds ?? EMPTY_PINNED;
  const isFavoritesLoading = favorites?.isLoading ?? false;
  const favoriteLabels = favorites ? labels.favorites : undefined;

  const [selected, setSelected] = useState<SelectedType | null>(null);
  const [filterCode, setFilterCode] = useState('');
  const [filterName, setFilterName] = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [showFullList, setShowFullList] = useState(false);
  // Domyślny filtr firmy już wstawiony do pola — domyślne zaznaczenie liczymy dopiero wtedy, żeby
  // wybrać wiersz widoczny przy tym filtrze, a nie przy jeszcze pustym.
  const [filterReady, setFilterReady] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelected(null);
      setFilterCode('');
      setFilterName('');
      setFilterCompany('');
      setShowFullList(false);
      setFilterReady(false);
    }
  }, [open]);

  // Domyślny filtr firmy przy otwarciu: nazwa firmy wskazanej przez nakładkę; puste pole = brak
  // zawężenia (wszystkie firmy).
  useEffect(() => {
    if (open) {
      setFilterCompany(companies.find((c) => c.id === defaultFilterCompanyId)?.name ?? '');
      setFilterReady(true);
    }
  }, [open, defaultFilterCompanyId, companies]);

  useEffect(() => {
    // `isFavoritesLoading` blokuje inicjalizację, dopóki przypięcia się nie rozwiążą — inaczej, gdy
    // typy (osobne zapytanie) dojdą PIERWSZE, ta gałąź widzi pusty zbiór przypięć, wymusza pełną
    // listę i nigdy tego nie koryguje (strażnik `selected !== null` blokuje ponowne przeliczenie).
    if (!open || !filterReady || selected !== null || types.length === 0 || isFavoritesLoading) {
      return;
    }

    if (initialTypeId) {
      const match = types.find(
        (type) =>
          type.id === initialTypeId &&
          (initialCompanyId == null || type.companyId === initialCompanyId),
      );
      if (match) {
        setSelected({ id: match.id, companyId: match.companyId });
        // Wiersz ma być WIDOCZNIE zaznaczony — widok „tylko ulubione" mógłby go ukryć. Pełną listę
        // wymuszamy tylko dla typu, który nie jest ulubiony.
        if (!pinnedIds.has(match.id)) setShowFullList(true);
        return;
      }
    }

    // Domyślnie zaznaczamy PIERWSZY WIDOCZNY typ: w widoku „tylko ulubione" — pierwszy przypięty
    // z bieżącego filtra firmy (inaczej zaznaczenie byłoby niewidoczne, a potwierdzenie nieaktywne);
    // w pełnej liście — pierwszy z filtra firmy (gdy filtr nic nie zostawia — pierwszy z całości).
    const inCompanyFilter = types.filter((type) =>
      type.companyName.toLowerCase().includes(filterCompany.toLowerCase()),
    );
    const pinnedInFilter = inCompanyFilter.filter((type) => pinnedIds.has(type.id));
    const showsPinnedOnly = favorites !== undefined && !showFullList && pinnedInFilter.length > 0;
    const first = showsPinnedOnly ? pinnedInFilter[0] : (inCompanyFilter[0] ?? types[0]);
    if (first) setSelected({ id: first.id, companyId: first.companyId });
  }, [
    open,
    filterReady,
    selected,
    types,
    initialTypeId,
    initialCompanyId,
    pinnedIds,
    isFavoritesLoading,
    favorites,
    showFullList,
    filterCompany,
  ]);

  // Baza do liczenia ulubionych i automatycznego powrotu do pełnej listy — zawężona do tekstu pola
  // „Firma", nie cała lista: ulubione z innej firmy nie mają znaczenia dla tego, co użytkownik widzi.
  const typesInCompanyFilter = useMemo(
    () =>
      types.filter((type) => type.companyName.toLowerCase().includes(filterCompany.toLowerCase())),
    [types, filterCompany],
  );

  // Brak ulubionych w wybranej firmie → pełna lista bez dodatkowego kliku (nigdy pusty ekran
  // „tylko ulubione"). Bez funkcji ulubionych lista jest zawsze pełna.
  const pinnedCount = typesInCompanyFilter.filter((type) => pinnedIds.has(type.id)).length;
  const effectiveShowFull = favorites === undefined || showFullList || pinnedCount === 0;

  const filteredTypes = typesInCompanyFilter.filter(
    (type) =>
      type.code.toLowerCase().includes(filterCode.toLowerCase()) &&
      type.name.toLowerCase().includes(filterName.toLowerCase()) &&
      (effectiveShowFull || pinnedIds.has(type.id)),
  );

  // Zaznaczony wiersz może wypaść z listy po zmianie filtra — potwierdzenie to blokuje, żeby nie
  // zatwierdzić wyboru, którego już nie widać.
  const isSelectedVisible =
    selected !== null &&
    filteredTypes.some((type) => type.id === selected.id && type.companyId === selected.companyId);

  const columnCount = favorites ? 6 : 5;

  const footer = (
    <div className="flex w-full items-center justify-between gap-2">
      {favorites && favoriteLabels ? (
        <Button
          variant={effectiveShowFull ? 'secondary' : 'ghost'}
          size="sm"
          leftIcon={
            <Star className="h-3.5 w-3.5" fill={effectiveShowFull ? 'none' : 'currentColor'} />
          }
          onClick={() => setShowFullList((v) => !v)}
          data-testid={`${testIdPrefix}-toggle-full-list`}
        >
          {effectiveShowFull ? favoriteLabels.toggleShowPinned : favoriteLabels.toggleShowFull}
        </Button>
      ) : (
        <span />
      )}
      <div className="flex items-center gap-2">
        <Button variant="secondary" onClick={onClose} data-testid={`${testIdPrefix}-cancel`}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="primary"
          disabled={!isSelectedVisible}
          onClick={() => {
            if (selected) onSelect(selected.id, selected.companyId);
          }}
          data-testid={`${testIdPrefix}-confirm`}
        >
          {labels.confirm}
        </Button>
      </div>
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} title={labels.title} size="xl" footer={footer}>
      {filterCompany.trim() === '' && (
        <p
          className="mb-2 px-1 text-sm text-text-secondary"
          data-testid={`${testIdPrefix}-all-mode-hint`}
        >
          {labels.allCompaniesHint}
        </p>
      )}

      {(isLoading || isFavoritesLoading) && (
        <p className="px-3 py-2 text-sm text-text-muted">{t('common.loading')}</p>
      )}
      {isError && (
        <p className="px-3 py-2 text-sm text-red-500" data-testid={`${testIdPrefix}-error`}>
          {t('common.errorLoading')}
        </p>
      )}
      {!isLoading && !isFavoritesLoading && !isError && (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border-primary text-left text-xs font-medium uppercase tracking-wide text-text-tertiary">
              <th className="w-8 pb-2 pl-1 pr-2" />
              {favorites && <th className="w-8 pb-2 pr-2" />}
              <th className="max-w-[8rem] truncate whitespace-nowrap pb-2 pr-4">
                {labels.colCode}
              </th>
              <th className="max-w-[16rem] truncate whitespace-nowrap pb-2 pr-4">
                {labels.colName}
              </th>
              <th className="max-w-[10rem] truncate whitespace-nowrap pb-2 pr-4">
                {labels.colCompany}
              </th>
              <th className="max-w-[16rem] truncate whitespace-nowrap pb-2">
                {labels.colDescription}
              </th>
            </tr>
            <tr className="border-b border-border-primary">
              <th className="pl-1 pr-2" />
              {favorites && <th className="pr-2" />}
              <th className="py-1 pr-4">
                <Input
                  size="sm"
                  value={filterCode}
                  onChange={(e) => setFilterCode(e.target.value)}
                  placeholder={labels.filterCode}
                  aria-label={labels.filterCode}
                  data-testid={`${testIdPrefix}-filter-code`}
                />
              </th>
              <th className="py-1 pr-4">
                <Input
                  size="sm"
                  value={filterName}
                  onChange={(e) => setFilterName(e.target.value)}
                  placeholder={labels.filterName}
                  aria-label={labels.filterName}
                  data-testid={`${testIdPrefix}-filter-name`}
                />
              </th>
              <th className="py-1 pr-4">
                <Input
                  size="sm"
                  value={filterCompany}
                  onChange={(e) => setFilterCompany(e.target.value)}
                  placeholder={labels.filterCompany}
                  aria-label={labels.filterCompany}
                  data-testid={`${testIdPrefix}-filter-company`}
                />
              </th>
              <th className="py-1" />
            </tr>
          </thead>
          <tbody>
            {filteredTypes.map((type) => {
              const isPinned = pinnedIds.has(type.id);
              const key = rowKey(type);
              const isRowSelected =
                selected !== null &&
                selected.id === type.id &&
                selected.companyId === type.companyId;
              return (
                <tr
                  key={key}
                  className="cursor-pointer hover:bg-bg-secondary"
                  onClick={() => setSelected({ id: type.id, companyId: type.companyId })}
                >
                  <td className="py-2 pl-1 pr-2">
                    {/* eslint-disable-next-line no-restricted-syntax -- radio w wierszu tabeli; RadioGroup renderuje karty, nie wiersze */}
                    <input
                      type="radio"
                      name={testIdPrefix}
                      value={key}
                      checked={isRowSelected}
                      onChange={() => setSelected({ id: type.id, companyId: type.companyId })}
                      className="accent-accent-600"
                      aria-label={`${type.name} (${type.code})`}
                      data-testid={`${testIdPrefix}-option-${key}`}
                    />
                  </td>
                  {favorites && favoriteLabels && (
                    <td className="py-2 pr-2">
                      <IconButton
                        size="sm"
                        aria-label={
                          isPinned ? favoriteLabels.unpinTooltip : favoriteLabels.pinTooltip
                        }
                        title={isPinned ? favoriteLabels.unpinTooltip : favoriteLabels.pinTooltip}
                        className={isPinned ? 'text-amber-500' : 'text-text-tertiary'}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isPinned) favorites.unpin(type.id);
                          else favorites.pin(type.id);
                        }}
                        data-testid={`${testIdPrefix}-pin-${key}`}
                      >
                        <Star fill={isPinned ? 'currentColor' : 'none'} />
                      </IconButton>
                    </td>
                  )}
                  <td className="max-w-[8rem] py-2 pr-4">
                    <div className="flex min-w-0 items-center gap-1.5" title={type.code}>
                      <span className="truncate whitespace-nowrap font-mono text-text-primary">
                        {type.code}
                      </span>
                      {type.origin && (
                        <span
                          className={
                            'inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ' +
                            (type.origin.isGlobal
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                              : 'bg-bg-tertiary text-text-secondary')
                          }
                          data-testid={`${testIdPrefix}-origin-${key}`}
                        >
                          {type.origin.label}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-2 pr-4 text-text-primary">
                    <span
                      className="block max-w-[16rem] truncate whitespace-nowrap"
                      title={type.name}
                    >
                      {type.name}
                    </span>
                  </td>
                  <td className="py-2 pr-4 text-text-primary">
                    <span
                      className="block max-w-[10rem] truncate whitespace-nowrap"
                      title={type.companyName}
                    >
                      {type.companyName}
                    </span>
                  </td>
                  <td className="py-2 text-text-primary">
                    <span
                      className="block max-w-[16rem] truncate whitespace-nowrap"
                      title={type.description ?? ''}
                    >
                      {type.description ?? ''}
                    </span>
                  </td>
                </tr>
              );
            })}
            {filteredTypes.length === 0 && (
              <tr>
                <td
                  colSpan={columnCount}
                  className="py-4 text-center text-text-muted"
                  data-testid={types.length === 0 ? `${testIdPrefix}-empty` : undefined}
                >
                  {types.length === 0 ? labels.empty : t('common.noResults')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </Modal>
  );
}
